import React, { useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { DIRECTORY_CAPABILITIES } from './directory-capabilities.js';
import { EMPLOYEE_CORE_FIELDS } from './employee-core-fields.js';
import { EMPLOYEE_DEPENDENCY_MAP } from './employee-dependency-map.js';
import { PENDING_REGISTRATION_SEAM } from './pending-registration-seam.js';
import { useDirectoryRead } from './use-directory-read.js';
import { useDirectoryDetail } from './use-directory-detail.js';
import { usePendingRegistrations } from './use-pending-registrations.js';
import DirectoryFilters from './DirectoryFilters.jsx';
import DirectoryGridSection from './DirectoryGridSection.jsx';
import DirectoryDetailCard from './DirectoryDetailCard.jsx';
import PendingRegistrationReview from './PendingRegistrationReview.jsx';
import { terminalLabel } from './directory-ui.js';
import Drawer from '../../shared/ui/Drawer.js';
import { useDiagMode } from '../../lib/use-diag-mode.js';

interface DirectoryPermissions {
  finance_input_rates?: boolean;
  hr_sensitive?: boolean;
  god_mode?: boolean;
  current_user?: { id?: string | number } | null;
  [key: string]: unknown;
}

interface Terminal {
  id: string | number;
  fedex_code?: string;
  [key: string]: unknown;
}

const VIEW_GRID = 'grid';
const VIEW_DETAIL = 'detail';
const VIEW_PENDING = 'pending';

export default function DirectoryModule() {
  const [view, setView] = useState<string>(VIEW_GRID);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | number | null>(null);
  const [diagEnabled] = useDiagMode();
  const {
    employees,
    terminals,
    grouped,
    groupedTerminalIds,
    unassigned,
    permissions,
    loading,
    error,
    search,
    statusFilter,
    terminalFilter,
    staffingFilter,
    setStatusFilter,
    setTerminalFilter,
    setStaffingFilter,
    handleSearch,
  } = useDirectoryRead();

  const perms = permissions as DirectoryPermissions;

  const {
    employee: detailEmployee,
    editLog,
    assets,
    loading: detailLoading,
    error: detailError,
    saving: detailSaving,
    reporting: detailReporting,
    roleSubmitting,
    saveBasicDetail,
    saveCompensationDetail,
    saveHrSensitiveDetail,
    reportAssetIssue,
    promoteEmployee,
    demoteEmployee,
    uploadPhoto,
    sendRegistrationInvite,
  } = useDirectoryDetail(view === VIEW_DETAIL ? selectedEmployeeId : null);

  const {
    requests: pendingRequests,
    loading: pendingLoading,
    error: pendingError,
    submittingId,
    reviewRequest,
  } = usePendingRegistrations(view === VIEW_PENDING);

  function openDetail(employee: { id: string | number; [key: string]: unknown }) {
    setSelectedEmployeeId(employee.id);
    setView(VIEW_DETAIL);
  }

  function openPendingReview() {
    setView(VIEW_PENDING);
  }

  function backToGrid() {
    setSelectedEmployeeId(null);
    setView(VIEW_GRID);
  }

  async function handleApprove(requestId: string | number) {
    const result = await reviewRequest(requestId, 'approve');
    if (result.ok) {
      setView(VIEW_PENDING);
    }
  }

  async function handleReject(requestId: string | number, reason: string) {
    const result = await reviewRequest(requestId, 'reject', reason);
    if (result.ok) {
      setView(VIEW_PENDING);
    }
  }

  function renderGridView() {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <div>
            <div style={{ color: C.text, fontSize: 16, fontWeight: 600 }}>Live directory read surface</div>
            <div style={{ color: C.textMuted, fontSize: 12, marginTop: 4 }}>
              {employees.length} employees loaded · {groupedTerminalIds.length} grouped terminals · {unassigned.length} unassigned
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={openPendingReview}
              style={{ padding: '8px 14px', borderRadius: 10, border: `1px solid rgba(201,162,39,0.3)`, background: 'rgba(201,162,39,0.12)', color: C.gold, cursor: 'pointer' }}
            >
              Pending review
            </button>
            <div style={{ color: C.textMuted, fontSize: 12 }}>
              finance rate access: <span style={{ color: perms.finance_input_rates ? C.green : C.textMuted }}>{perms.finance_input_rates ? 'yes' : 'no'}</span>
            </div>
          </div>
        </div>

        <DirectoryFilters
          terminals={terminals as Array<{ id: string | number; name?: string; label?: string }>}
          search={search}
          statusFilter={statusFilter}
          terminalFilter={terminalFilter}
          staffingFilter={staffingFilter}
          onSearchChange={handleSearch}
          onStatusChange={setStatusFilter}
          onTerminalChange={setTerminalFilter}
          onStaffingChange={setStaffingFilter}
        />

        {loading ? <div style={{ color: C.textMuted, marginTop: 16 }}>Loading directory...</div> : null}
        {error ? <div style={{ color: C.red, marginTop: 16 }}>Directory load failed: {error}</div> : null}

        {!loading && !error ? (
          <div style={{ marginTop: 16, display: 'grid', gap: 14 }}>
            {groupedTerminalIds.length === 0 && unassigned.length === 0 ? (
              <div style={{ color: C.textMuted, fontSize: 13 }}>No employees found for the current filters.</div>
            ) : null}

            {groupedTerminalIds.map((terminalId) => {
              const terminal = (terminals as Terminal[]).find((item) => String(item.id) === String(terminalId));
              const terminalEmployees = ((grouped as Record<string, unknown[]>)[terminalId] || []) as Array<{ id: string | number; [key: string]: unknown }>;

              return (
                <DirectoryGridSection
                  key={terminalId}
                  title={terminalLabel(terminal)}
                  subtitle={terminal?.fedex_code ? `FedEx code: ${terminal.fedex_code}` : 'Grouped directory read surface'}
                  employees={terminalEmployees}
                  onEmployeeSelect={openDetail}
                />
              );
            })}

            {unassigned.length > 0 ? (
              <DirectoryGridSection
                title="Unassigned"
                subtitle="Employees without a current terminal assignment"
                employees={unassigned as Array<{ id: string | number; [key: string]: unknown }>}
                onEmployeeSelect={openDetail}
              />
            ) : null}
          </div>
        ) : null}
      </>
    );
  }

  function renderActiveView() {
    if (view === VIEW_PENDING) {
      return (
        <PendingRegistrationReview
          requests={pendingRequests as Array<{ id: string | number; first_name?: string; last_name?: string; driver_id?: string; email?: string; phone?: string; created_at?: string }>}
          loading={pendingLoading}
          error={pendingError}
          submittingId={submittingId}
          onApprove={handleApprove}
          onReject={handleReject}
          onSendInvite={sendRegistrationInvite}
          onBack={backToGrid}
        />
      );
    }

    return renderGridView();
  }

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted, marginBottom: 8 }}>
        Directory module boundary
      </div>
      <p style={{ marginTop: 0, lineHeight: 1.6 }}>
        Directory is not just another tab. It is the people-data backbone for the portal ecosystem. These first V2 slices move the real read surfaces over without importing mutation complexity.
      </p>

      <div style={LAYOUT.subPanel}>
        {renderActiveView()}
      </div>

      {diagEnabled && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted, marginBottom: 8 }}>
            Directory capability map
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {DIRECTORY_CAPABILITIES.map((item) => (
              <div key={item.id} style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
                <div style={{ color: C.gold, fontSize: 14, marginBottom: 4 }}>{item.label}</div>
                <div style={{ color: C.textMuted, fontSize: 12, marginBottom: 4 }}>role: {item.role}</div>
                <div style={{ color: C.textMuted, fontSize: 12 }}>feeds: {item.feeds.join(', ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={LAYOUT.subPanel}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted, marginBottom: 8 }}>
          Employee core field map
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          {EMPLOYEE_CORE_FIELDS.map((group) => (
            <div key={group.group} style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
              <div style={{ color: C.gold, fontSize: 14, marginBottom: 6 }}>{group.group}</div>
              <div style={{ color: C.textMuted, fontSize: 12 }}>{group.fields.join(', ')}</div>
            </div>
          ))}
        </div>
      </div>

      {diagEnabled && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted, marginBottom: 8 }}>
            Downstream dependency map
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {EMPLOYEE_DEPENDENCY_MAP.map((group) => (
              <div key={group.group} style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
                <div style={{ color: C.gold, fontSize: 14, marginBottom: 6 }}>{group.group}</div>
                <div style={{ color: C.textMuted, fontSize: 12 }}>consumed by: {group.consumedBy.join(', ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Drawer
        open={view === VIEW_DETAIL}
        onClose={backToGrid}
        title="Employee detail"
        width={760}
      >
        {detailLoading ? (
          <div style={{ color: C.textMuted }}>Loading employee detail...</div>
        ) : detailError ? (
          <div style={{ color: C.red }}>Directory detail failed: {detailError}</div>
        ) : (
          <DirectoryDetailCard
            employee={detailEmployee}
            currentUser={(perms.current_user as { id?: string | number } | null) ?? null}
            assets={assets as Array<{ id: string | number; asset_type?: string; asset_label?: string; serial_number?: string; [key: string]: unknown }>}
            editLog={editLog as Array<{ id: string | number; field_name?: string; old_value?: string; new_value?: string; changed_by_email?: string; created_at?: string }>}
            canEditCompensation={!!perms.finance_input_rates}
            canEditHrSensitive={!!perms.hr_sensitive}
            canManageRoles={!!perms.god_mode}
            saving={detailSaving}
            reporting={detailReporting}
            roleSubmitting={roleSubmitting}
            onSave={saveBasicDetail}
            onSaveCompensation={saveCompensationDetail}
            onSaveHrSensitive={saveHrSensitiveDetail}
            onReportAsset={reportAssetIssue}
            onPromote={promoteEmployee}
            onDemote={demoteEmployee}
            onUploadPhoto={uploadPhoto as (data: string | ArrayBuffer | null) => Promise<unknown>}
            onBack={backToGrid}
          />
        )}
      </Drawer>

      {diagEnabled && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted, marginBottom: 8 }}>
            Pending registration seam
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
              <div style={{ color: C.gold, fontSize: 14, marginBottom: 6 }}>entry</div>
              <div style={{ color: C.textMuted, fontSize: 12 }}>{PENDING_REGISTRATION_SEAM.entry}</div>
            </div>
            <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
              <div style={{ color: C.gold, fontSize: 14, marginBottom: 6 }}>review surface</div>
              <div style={{ color: C.textMuted, fontSize: 12 }}>{PENDING_REGISTRATION_SEAM.reviewSurface}</div>
            </div>
            <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
              <div style={{ color: C.gold, fontSize: 14, marginBottom: 6 }}>approval action</div>
              <div style={{ color: C.textMuted, fontSize: 12 }}>{PENDING_REGISTRATION_SEAM.approvalAction}</div>
            </div>
            <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
              <div style={{ color: C.gold, fontSize: 14, marginBottom: 6 }}>downstream effects</div>
              <div style={{ color: C.textMuted, fontSize: 12 }}>{PENDING_REGISTRATION_SEAM.downstreamEffects.join(', ')}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
